import { expenseDateValidation } from "../../../../utils/validation/expenseDateValidation";
import * as Yup from "yup";

import {
  AmountAtLeastOne,
  AmountPositiveRequired,
  AmountRequired,
  auto,
  bike,
  bus,
  cab,
  car,
  DateRequired,
  DistanceNumberRequired,
  DistancePositiveRequired,
  fileSize,
  FileSizeTooLarge,
  fileType,
  FormRequired,
  InvalidDateFormat,
  InvalidMode,
  metro,
  ModeRequired,
  others,
  ReasonRequired,
  rikshaw,
  TravelFromRequired,
  TravelToRequired,
  UnsupportedFormated,
} from "../../../../utils/Constant";

const conveyanceModeLimits: Record<
  string,
  { ratePerKm: number; label: string }
> = {
  [cab]: { ratePerKm: 20, label: "Cab" },
  [auto]: { ratePerKm: 15, label: "Auto" },
};

export const conveyanceManagementvalidationSchema = Yup.object({
  forms: Yup.array()
    .of(
      Yup.object({
        date: expenseDateValidation(DateRequired, InvalidDateFormat),

        reason: Yup.string().required(ReasonRequired),

        source: Yup.string().required(TravelFromRequired),

        destination: Yup.string().required(TravelToRequired),

        mode: Yup.string()
          .required(ModeRequired)
          .oneOf(
            [car, bike, cab, rikshaw, bus, metro, auto, others],
            InvalidMode
          ),

        distance: Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null
              ? undefined
              : value
          )
          .when(["mode"], (values, schema) => {
            const [mode] = values;

            return mode
              ? schema
                  .required("Distance is required")
                  .typeError(DistanceNumberRequired)
                  .positive(DistancePositiveRequired)
              : schema.test(
                  "mode-not-selected",
                  "Please select Mode first to calculate the amount",
                  (value) => value === undefined || value === null
                );
          }),

        amount: Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null
              ? undefined
              : value
          )
          .required(AmountRequired)
          .typeError(AmountPositiveRequired)
          .min(1, AmountAtLeastOne)
          .when(["mode", "distance"], (values, schema) => {
            const [mode, distance] = values;
            const modeLimit = conveyanceModeLimits[String(mode)];
            const numericDistance = Number(distance);

            if (!modeLimit || !Number.isFinite(numericDistance)) {
              return schema;
            }

            const maxAmount = numericDistance * modeLimit.ratePerKm;

            return schema.max(
              maxAmount,
              `${modeLimit.label} amount cannot exceed ₹${maxAmount} for ${numericDistance} km`
            );
          }),

        bill: Yup.array()
          .transform((_, originalValue) => {
            if (!originalValue) return [];
            return Array.isArray(originalValue)
              ? originalValue
              : [originalValue];
          })
          .of(
            Yup.mixed<File>()
              .test(fileSize, FileSizeTooLarge, (value) => {
                if (!value || !(value instanceof File)) {
                  return true;
                }

                return value.size <= 5 * 1024 * 1024;
              })
              .test(fileType, UnsupportedFormated, (value) => {
                if (!value || !(value instanceof File)) {
                  return true;
                }

                return [
                  "image/jpg",
                  "image/jpeg",
                  "image/png",
                  "application/pdf",
                ].includes(value.type);
              })
          )
          .when(["mode"], (values, schema) => {
            const [mode] = values;

            return [cab, auto].includes(mode)
              ? schema
                  .min(
                    1,
                    `Bill is required when mode is ${
                      mode === cab ? "Cab" : "Auto"
                    }`
                  )
                  .required(
                    `Bill is required when mode is ${
                      mode === cab ? "Cab" : "Auto"
                    }`
                  )
              : schema.notRequired();
          }),
      })
    )
    .min(1, "At least one bill is required")
    .required(FormRequired),
});
